import { useState, useEffect, Fragment } from 'react'
import { useSelector } from 'react-redux'
import PropTypes from 'prop-types'
import axios from 'axios'
import PerfectScrollbar from 'react-perfect-scrollbar'
import credentialsApi from '../../api/credentials'
import { baseURL } from '../../store/constant'

// Tailwind CSS
import 'react-perfect-scrollbar/dist/css/styles.css'
import { RootState } from '@/store'

const fetchList = async ({ name, nodeData }) => {
    const loadMethod = nodeData.inputParams.find((param) => param.name === name)?.loadMethod
    const username = localStorage.getItem('username')
    const password = localStorage.getItem('password')

    let lists = await axios
        .post(
            `${baseURL}/api/v1/node-load-method/${nodeData.name}`,
            { ...nodeData, loadMethod },
            { auth: username && password ? { username, password } : undefined }
        )
        .then(async function (response) {
            return response.data
        })
        .catch(function (error) {
            console.error(error)
        })
    return lists
}

export const AsyncDropdown = ({
    name,
    nodeData,
    value,
    onSelect,
    isCreateNewOption,
    onCreateNew,
    credentialNames = [],
    disabled = false,
    disableClearable = false
}) => {
    const customization = useSelector((state: RootState) => state.customization)

    const [open, setOpen] = useState(false)
    const [options, setOptions] = useState<any>([])
    const [loading, setLoading] = useState(false)
    const findMatchingOptions: any = (options = [], value) => options.find((option: any) => option.name === value)
    const getDefaultOptionValue = () => ''
    const addNewOption = [{ label: '- Create New -', name: '-create-' }]
    const [internalValue, setInternalValue] = useState<any>(value ?? '')

    const fetchCredentialList = async () => {
        try {
            let names = ''
            if (credentialNames.length > 1) {
                names = credentialNames.join('&credentialName=')
            } else {
                names = credentialNames[0]
            }
            const resp = await credentialsApi.getCredentialsByName(names)
            if (resp.data) {
                const returnList = resp.data.map(item => ({
                    label: item.name,
                    name: item.id
                }))
                return returnList
            }
        } catch (error) {
            console.error(error)
        }
    }

    useEffect(() => {
        setLoading(true)
        ;(async () => {
            const fetchData = async () => {
                const response: any = credentialNames.length ? await fetchCredentialList() : await fetchList({ name, nodeData })
                if (isCreateNewOption) setOptions([...response, ...addNewOption])
                else setOptions([...response])
                setLoading(false)
            }
            fetchData()
        })()

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    return (
        <div className="relative w-full">
            <input
                id={name}
                disabled={disabled}
                className="w-full border rounded-md px-3 py-2 text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={findMatchingOptions(options, internalValue)?.label || getDefaultOptionValue()}
                onFocus={() => setOpen(true)}
                onBlur={() => setOpen(false)}
                onChange={(e) => {
                    const value = e.target.value
                    setInternalValue(value)
                    onSelect(value)
                }}
                placeholder="Select an option"
            />
            {open && (
                <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
                    <PerfectScrollbar>
                        <ul className="list-none p-0 m-0">
                            {loading && (
                                <li className="flex items-center justify-center p-4">
                                    <div className="spinner-border animate-spin h-5 w-5 border-4 border-blue-500 border-t-transparent rounded-full"></div>
                                </li>
                            )}
                            {options.map((option: any, index) => (
                                <li
                                    key={index}
                                    className={`cursor-pointer p-2 hover:bg-gray-100 ${internalValue === option.name ? 'bg-blue-100' : ''}`}
                                    onClick={() => {
                                        if (option.name === '-create-') {
                                            onCreateNew()
                                        } else {
                                            setInternalValue(option.name)
                                            onSelect(option.name)
                                        }
                                    }}
                                >
                                    <div className="flex flex-col">
                                        <span className="font-medium">{option.label}</span>
                                        {option.description && (
                                            <span className={`text-sm ${customization.isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                                                {option.description}
                                            </span>
                                        )}
                                    </div>
                                </li>
                            ))}
                        </ul>
                    </PerfectScrollbar>
                </div>
            )}
        </div>
    )
}

AsyncDropdown.propTypes = {
    name: PropTypes.string,
    nodeData: PropTypes.object,
    value: PropTypes.string,
    onSelect: PropTypes.func,
    onCreateNew: PropTypes.func,
    disabled: PropTypes.bool,
    credentialNames: PropTypes.array,
    disableClearable: PropTypes.bool,
    isCreateNewOption: PropTypes.bool
}
